import { useState, useRef } from 'react'
import { Upload, Download, X } from 'lucide-react'
import Card from '../components/Card'
import Button from '../components/Button'

export default function ImageResizer() {
  const [selectedFile, setSelectedFile] = useState(null)
  const [resizedImage, setResizedImage] = useState(null)
  const [width, setWidth] = useState('')
  const [height, setHeight] = useState('')
  const [maintainAspectRatio, setMaintainAspectRatio] = useState(true)
  const [originalDimensions, setOriginalDimensions] = useState({ width: 0, height: 0 })
  const fileInputRef = useRef(null)

  const handleFileSelect = (file) => {
    if (!file.type.startsWith('image/')) {
      alert('Please select a valid image file.')
      return
    }

    const reader = new FileReader()
    reader.onload = (e) => {
      const img = new Image()
      img.onload = () => {
        setOriginalDimensions({ width: img.width, height: img.height })
        setWidth(img.width)
        setHeight(img.height)
        setSelectedFile(file)
        setResizedImage(null)
      }
      img.src = e.target.result
    }
    reader.readAsDataURL(file)
  }

  const resizeImage = () => {
    if (!selectedFile || !width || !height) return

    const img = new Image()
    const reader = new FileReader()

    reader.onload = (e) => {
      img.onload = () => {
        const canvas = document.createElement('canvas')
        canvas.width = parseInt(width)
        canvas.height = parseInt(height)
        const ctx = canvas.getContext('2d')
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height)

        canvas.toBlob((blob) => {
          const url = URL.createObjectURL(blob)
          setResizedImage({ url, blob })
        }, selectedFile.type, 0.95)
      }
      img.src = e.target.result
    }

    reader.readAsDataURL(selectedFile)
  }

  const handleWidthChange = (newWidth) => {
    setWidth(newWidth)
    if (maintainAspectRatio && originalDimensions.width > 0) {
      const ratio = originalDimensions.height / originalDimensions.width
      setHeight(Math.round(parseInt(newWidth) * ratio).toString())
    }
  }

  const handleHeightChange = (newHeight) => {
    setHeight(newHeight)
    if (maintainAspectRatio && originalDimensions.height > 0) {
      const ratio = originalDimensions.width / originalDimensions.height
      setWidth(Math.round(parseInt(newHeight) * ratio).toString())
    }
  }

  const downloadImage = () => {
    if (!resizedImage) return

    const link = document.createElement('a')
    link.href = resizedImage.url
    link.download = `resized-${selectedFile.name}`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  return (
    <div className="space-y-6">
      <Card
        className="cursor-pointer border-gray-300 hover:border-primary-400"
        onClick={() => fileInputRef.current?.click()}
      >
        <Card.Body className="text-center py-8">
          {!selectedFile ? (
            <div>
              <Upload className="w-12 h-12 text-indigo-600 mx-auto mb-4" />
              <p className="text-base font-semibold text-gray-900">Click to upload image</p>
              <p className="text-sm text-gray-500 mt-1">Select an image to resize</p>
            </div>
          ) : (
            <div className="flex items-center justify-center gap-4">
              <div className="text-left">
                <p className="text-base font-semibold text-gray-900">{selectedFile.name}</p>
                <p className="text-sm text-gray-500">
                  Original: {originalDimensions.width} × {originalDimensions.height}px
                </p>
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  setSelectedFile(null)
                  setResizedImage(null)
                  setWidth('')
                  setHeight('')
                }}
                className="ml-auto p-2 hover:bg-gray-200 rounded-lg"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
          )}
        </Card.Body>
      </Card>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          if (e.target.files?.[0]) handleFileSelect(e.target.files[0])
        }}
      />

      {selectedFile && (
        <>
          <Card>
            <Card.Body>
              <div className="mb-4">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={maintainAspectRatio}
                    onChange={(e) => setMaintainAspectRatio(e.target.checked)}
                    className="mr-2"
                  />
                  <span className="text-sm">Maintain aspect ratio</span>
                </label>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="label">Width (px)</label>
                  <input
                    type="number"
                    value={width}
                    onChange={(e) => handleWidthChange(e.target.value)}
                    className="input w-full"
                    min="1"
                  />
                </div>
                <div>
                  <label className="label">Height (px)</label>
                  <input
                    type="number"
                    value={height}
                    onChange={(e) => handleHeightChange(e.target.value)}
                    className="input w-full"
                    min="1"
                  />
                </div>
              </div>

              <Button onClick={resizeImage} variant="primary" className="w-full">
                Resize Image
              </Button>
            </Card.Body>
          </Card>

          {resizedImage && (
            <Card>
              <Card.Body>
                <div className="text-center space-y-4">
                  <div className="inline-block p-4 bg-gray-100 rounded-lg">
                    <img src={resizedImage.url} alt="Resized" className="max-w-full h-auto max-h-96" />
                  </div>
                  <Button onClick={downloadImage} variant="primary" size="lg">
                    <Download className="w-5 h-5 mr-2" />
                    Download Resized Image
                  </Button>
                </div>
              </Card.Body>
            </Card>
          )}
        </>
      )}
    </div>
  )
}

